import React from 'react';
import { FaUsers, FaAward, FaShieldAlt, FaChartLine, FaHeart, FaRocket } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const About: React.FC = () => {
  const values = [
    {
      icon: FaShieldAlt,
      title: 'Transparence',
      description: 'Nous testons chaque bookmaker en conditions réelles et partageons nos résultats sans parti pris.',
    },
    {
      icon: FaChartLine,
      title: 'Expertise',
      description: 'Notre équipe analyse en profondeur les cotes, bonus et fonctionnalités de chaque plateforme.',
    },
    {
      icon: FaAward,
      title: 'Indépendance',
      description: 'Nous ne sommes affiliés à aucun bookmaker. Nos recommandations sont 100% objectives.',
    },
    {
      icon: FaHeart,
      title: 'Engagement',
      description: 'Votre satisfaction et votre sécurité sont notre priorité absolue.',
    },
  ];

  const team = [
    {
      name: 'Équipe d\'Experts',
      role: 'Analystes de Paris Sportifs',
      description: 'Nos experts testent quotidiennement les bookmakers pour vous offrir les meilleures recommandations.',
    },
    {
      name: 'Testeurs Indépendants',
      role: 'Vérification en Conditions Réelles',
      description: 'Chaque bookmaker est testé en conditions réelles : dépôts, paris, retraits et support client.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'À Propos de Parismin',
    description: 'Découvrez qui nous sommes, notre mission et nos valeurs. Parismin est votre guide expert pour les paris sportifs en France.',
    publisher: {
      '@type': 'Organization',
      name: 'Parismin',
      url: 'https://parismin.com',
    },
  };

  return (
    <Layout>
      <SEO
        title="À Propos de Parismin - Votre Guide Expert"
        description="Découvrez Parismin, votre guide de confiance pour les paris sportifs en France. Notre mission, nos valeurs et notre engagement envers vous."
        url="/about"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block mb-6 px-4 py-2 glass-card rounded-full text-sm font-semibold border border-cyan-500/30">
            <FaUsers className="inline mr-2 text-cyan-400" />
            Notre Histoire
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-6 heading-font">
            <span className="text-gradient">À Propos de Parismin</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Votre guide de confiance pour les paris sportifs en France
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="glass-card rounded-3xl p-10 md:p-16 mb-16">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <FaRocket className="text-white text-3xl" />
                </div>
                <h2 className="text-4xl md:text-5xl font-black mb-6 heading-font">
                  <span className="text-gradient">Notre Mission</span>
                </h2>
              </div>
              <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
                <p>
                  Parismin est né d'une simple constatation : choisir le bon bookmaker peut être un véritable casse-tête. 
                  Avec des dizaines d'opérateurs disponibles, des bonus alléchants et des promesses parfois trop belles 
                  pour être vraies, il est difficile de s'y retrouver.
                </p>
                <p>
                  Notre mission est de vous simplifier la vie en testant chaque bookmaker en conditions réelles. 
                  Nous déposons de l'argent réel, plaçons des paris, testons les retraits et évaluons le support client. 
                  Pas de théorie, uniquement des faits basés sur notre expérience.
                </p>
                <p>
                  Nous croyons fermement que chaque parieur mérite d'avoir accès à des informations transparentes et 
                  objectives pour prendre les meilleures décisions. C'est pourquoi nous restons totalement indépendants 
                  et ne favorisons aucun bookmaker.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6 heading-font">
              <span className="text-gradient">Nos Valeurs</span>
            </h2>
            <p className="text-gray-400 text-xl max-w-2xl mx-auto">
              Les principes qui guident notre travail chaque jour
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="glass-card rounded-3xl p-8 text-center card-hover group"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <Icon className="text-3xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 heading-font">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6 heading-font">
              <span className="text-gradient">Notre Équipe</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="glass-card rounded-3xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <FaUsers className="text-white text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2 heading-font">{member.name}</h3>
                    <p className="text-cyan-400 font-semibold mb-3">{member.role}</p>
                    <p className="text-gray-300 leading-relaxed">{member.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust Us Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="glass-card rounded-3xl p-10 md:p-16 max-w-5xl mx-auto border-2 border-cyan-500/30">
            <div className="text-center mb-10">
              <h2 className="text-4xl md:text-5xl font-black mb-6 heading-font">
                <span className="text-gradient">Pourquoi Nous Faire Confiance ?</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { number: '100+', label: 'Bookmakers Testés' },
                { number: '50K+', label: 'Parieurs Aidés' },
                { number: '5+', label: 'Années d\'Expérience' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-black text-gradient-gold mb-2">{stat.number}</div>
                  <div className="text-gray-400 font-semibold">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;

